import styled from '@emotion/styled';
import { Table, TableColumnsType, Tooltip } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { DetailTitreEntity } from '../../../../../entities/detailTitre.entity';
import { DetenteurEntity } from '../../../../../entities/detenteur.entity';
import { TitreEntity } from '../../../../../entities/titre.entity';
import { PRIMARY, YELLOW } from '../../../../../shared/colors';
import { formatNum } from '../../../../../shared/constants';
import { getUserPortefeuille, getUserPortefeuilleKPI } from '../network';

const Container = styled.div`
  height: 100%;
  box-sizing: border-box;
  padding: 8px 10px 10px;
  color: var(--mt-text);
  overflow-x: auto;
  overflow-y: auto;

  tr.ant-table-expanded-row > td,
  tr.ant-table-expanded-row:hover > td {
    background-color: var(--mt-surface-strong);
  }
  p {
    margin: 0px;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-family: Poppins-B;
    font-size: 11px;
    font-weight: normal;
    text-transform: uppercase;
  }

  .monitor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .monitor-title {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .monitor-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .refresh-icon {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background-color: var(--mt-surface);
    cursor: pointer;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(120px, 1fr));
    gap: 8px;
    margin-bottom: 10px;
  }

  .kpi-card {
    min-width: 0;
    padding: 9px 10px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-surface);
  }

  .kpi-card span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    text-transform: uppercase;
  }

  .kpi-card strong {
    display: block;
    margin-top: 3px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .section-heading {
    margin: 8px 0;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 12px;
    text-transform: uppercase;
  }

  .empty-state {
    display: grid;
    min-height: 180px;
    place-items: center;
    color: var(--mt-muted);
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-surface);
  }
`;

export const Portfolio = ({
  getTitre,
}: {
  getTitre: (b: TitreEntity, ope: '1' | '2', detUrl: string) => void;
}) => {
  const connectedUser = useSelector((state: any) => state.userReducer).user;
  const [portefeuilleKPI, setPortefeuilleKPI] = useState<any>();
  const [detenteursPF, setDetenteursPF] = useState<DetenteurEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // const [userVentes, setUserVentes] = useState<VenteEntity[]>([]);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);

    await getUserPortefeuille(connectedUser.id).then(({ data }) => {
      setDetenteursPF(data);
    });

    await getUserPortefeuilleKPI(connectedUser.id).then(({ data }) => {
      setPortefeuilleKPI(data);
    });
    setIsLoading(false);
  };

  return (
    <Container>
      <div
        className='monitor-toolbar'
      >
        <span className='monitor-title'>Portefeuille consolidé</span>
        <div className='monitor-actions'>
          <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />
          <Tooltip title='Rafraichir' placement='leftTop' color={YELLOW}>
            <span className='refresh-icon' onClick={fetchData}>
              <FiRefreshCcw />
            </span>
          </Tooltip>
        </div>
      </div>

      <div className='kpi-grid'>
        <div className='kpi-card'>
          <span>Valeur portefeuille</span>
          <strong>
            {portefeuilleKPI
              ? `${formatNum(portefeuilleKPI?.valeurPortefeuille)} XAF`
              : '--'}
          </strong>
        </div>
        <div className='kpi-card'>
          <span>Rendement</span>
          <strong>
            {portefeuilleKPI
              ? Math.round(
                  (portefeuilleKPI?.rendement * (1 + 0.001 / 100) +
                    Number.EPSILON) *
                    100,
                ) / 100
              : '--'}{' '}
            %
          </strong>
        </div>
        <div className='kpi-card'>
          <span>PNL</span>
          <strong>{portefeuilleKPI ? `${formatNum(portefeuilleKPI?.pnl)} XAF` : '--'}</strong>
        </div>
        <div className='kpi-card'>
          <span>Valeur marché</span>
          <strong>
            {portefeuilleKPI
              ? Math.round(
                  (portefeuilleKPI?.valeurMarche * (1 + 0.001 / 100) +
                    Number.EPSILON) *
                    100,
                ) / 100
              : '--'}{' '}
            %
          </strong>
        </div>
        <div className='kpi-card'>
          <span>Duration</span>
          <strong>
            {portefeuilleKPI
              ? Math.round(
                  (portefeuilleKPI?.duration * (1 + 0.001 / 100) +
                    Number.EPSILON) *
                    100,
                ) / 100
              : '--'}{' '}
            ans
          </strong>
        </div>
      </div>

      <div className='section-heading'>Mon portefeuille</div>

      {detenteursPF.length === 0 ? (
        <div className='empty-state'>Votre portefeuille est présentement vide</div>
      ) : (
        <div>
          <Table
            columns={COLUMNS}
            scroll={{ x: 'max-content' }}
            dataSource={detenteursPF}
            expandable={{
              expandedRowRender: (row) => {
                return (
                  <Table<DetailTitreEntity>
                    columns={SUB_COLUMNS}
                    dataSource={(row as any)?.detailTitres}
                    scroll={{ x: 'max-content' }}
                    rowClassName={(row, index) =>
                      index % 2 === 0 ? 'even-row' : 'odd-row'
                    }
                    rowKey='id'
                    size='small'
                    style={{ zoom: 0.9 }}
                    pagination={false}
                    rowSelection={{
                      type: 'radio',
                      onChange: (selectedRowKeys, selectedRows) => {
                        getTitre(selectedRows[0].titre, '2', row.url);
                      },
                    }}
                  />
                );
              },
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <FaMinus
                    onClick={(e: any) => onExpand(record, e)}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <FaPlus
                    onClick={(e: any) => onExpand(record, e)}
                    style={{ cursor: 'pointer' }}
                  />
                ),
            }}
            rowClassName={(row, index) =>
              index % 2 === 0 ? 'even-row' : 'odd-row'
            }
            rowKey='id'
            size='small'
            style={{ zoom: 0.8 }}
            pagination={false}
          />
        </div>
      )}
    </Container>
  );
};

const COLUMNS = [
  {
    title: 'Code',
    dataIndex: 'code_referent',
  },
  {
    title: 'Détenteur',
    render: (cell: any, row: DetenteurEntity) => `${row.nom} ${row.prenom}`,
  },
  {
    title: 'Gestionnaire',
    render: (cell: any, row: any) =>
      `${row.createur.nom} ${row.createur.prenom}`,
  },
  {
    title: 'Qte titre',
    render: (cell: any, row: any) => `${formatNum(row.qteTotal)}`,
  },
  {
    title: 'Montant',
    render: (cell: any, row: any) => `${formatNum(row.montantTotal)}`,
  },
  // {
  //   title: 'P&L',
  //   dataIndex: 'pnl',
  //   render: (cell: any, row: DetenteurEntity) => (
  //     <span style={cell > 0 ? { color: '#008000' } : { color: '#e57373' }}>
  //       {formatNum(cell)}
  //     </span>
  //   ),
  // },
];

const SUB_COLUMNS: TableColumnsType<DetailTitreEntity> = [
  {
    title: 'Pays',
    render: (cell: any, row: DetailTitreEntity) => row.titre.pays,
  },
  {
    title: 'Code Titre',
    render: (cell: any, row: DetailTitreEntity) => row.parentCode,
  },
  {
    title: 'Date acquisition',
    dataIndex: 'dateAcquisition',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date maturité',
    render: (cell: any, row: DetailTitreEntity) =>
      `${moment(row.titre.dateMaturite).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Qte',
    dataIndex: 'qte',
  },
  {
    title: 'Prix',
    dataIndex: 'prixNominale',
    render: (cell: any) => `${formatNum(cell * 1)}`,
  },
  {
    title: 'Montant',
    dataIndex: 'montant',
    render: (cell: any) => `${formatNum(cell * 1)}`,
  },
  {
    title: 'Décote',
    dataIndex: 'decote',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxInteret',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Rendement',
    dataIndex: 'tauxRendement',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Fréquence',
    dataIndex: 'frequence',
  },
  // {
  //   title: 'P&L',
  //   dataIndex: 'pnl',
  //   render: (cell: any, row: DetailTitreEntity) => (
  //     <span style={cell > 0 ? { color: '#008000' } : { color: '#e57373' }}>
  //       {formatNum(cell)}
  //     </span>
  //   ),
  // },
];
